import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Component for managing relationships between wiki entries
 */
const RelationshipEditor = ({
  entryId,
  currentRelationships = [],
  onRelationshipChange,
  entryType,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState('');
  const [relationshipType, setRelationshipType] = useState('related');
  const [availableEntries, setAvailableEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Define relationship types based on entry type
  const getRelationshipTypes = () => {
    const baseTypes = [
      { value: 'related', label: 'Related To' },
      { value: 'mentioned_in', label: 'Mentioned In' },
      { value: 'appeared_in', label: 'Appeared In' },
    ];
    
    switch (entryType) {
      case 'person':
        return [
          ...baseTypes,
          { value: 'ally', label: 'Ally' },
          { value: 'enemy', label: 'Enemy' },
          { value: 'rival', label: 'Rival' },
          { value: 'friend', label: 'Friend' },
          { value: 'family', label: 'Family Member' },
          { value: 'parent', label: 'Parent' },
          { value: 'child', label: 'Child' },
          { value: 'sibling', label: 'Sibling' },
          { value: 'spouse', label: 'Spouse' },
          { value: 'subordinate', label: 'Subordinate' },
          { value: 'leader', label: 'Leader' },
          { value: 'mentor', label: 'Mentor' },
          { value: 'student', label: 'Student' },
          { value: 'owns_item', label: 'Owns Item' },
          { value: 'owns_location', label: 'Owns Location' },
          { value: 'previously_owned', label: 'Previously Owned' },
          { value: 'seeks', label: 'Seeks' },
          { value: 'protects', label: 'Protects' },
          { value: 'hunts', label: 'Hunts' },
          { value: 'lives_in', label: 'Lives In' },
          { value: 'works_at', label: 'Works At' },
          { value: 'member_of', label: 'Member Of' },
        ];
      case 'item':
        return [
          ...baseTypes,
          { value: 'owned_by', label: 'Owned By' },
          { value: 'previously_owned_by', label: 'Previously Owned By' },
          { value: 'created_by', label: 'Created By' },
          { value: 'discovered_by', label: 'Discovered By' },
          { value: 'stolen_by', label: 'Stolen By' },
          { value: 'sought_by', label: 'Sought By' },
          { value: 'protected_by', label: 'Protected By' },
          { value: 'located_at', label: 'Located At' },
          { value: 'held_by', label: 'Held By' },
          { value: 'hidden_in', label: 'Hidden In' },
          { value: 'found_in', label: 'Found In' },
          { value: 'part_of', label: 'Part Of' },
          { value: 'contains', label: 'Contains' },
        ];
      case 'location':
        return [
          ...baseTypes,
          { value: 'contains', label: 'Contains' },
          { value: 'part_of', label: 'Part Of' },
          { value: 'connected_to', label: 'Connected To' },
          { value: 'near', label: 'Near' },
          { value: 'parent_location', label: 'Within' },
          { value: 'child_location', label: 'Contains Location' },
          { value: 'owned_by', label: 'Owned By' },
          { value: 'ruled_by', label: 'Ruled By' },
          { value: 'protected_by', label: 'Protected By' },
          { value: 'threatened_by', label: 'Threatened By' },
          { value: 'home_to', label: 'Home To' },
          { value: 'workplace_of', label: 'Workplace Of' },
          { value: 'headquarters_of', label: 'Headquarters Of' },
          { value: 'encounter_with', label: 'Encounter With' },
        ];
      case 'monster':
        return [
          ...baseTypes,
          { value: 'enemy_of', label: 'Enemy Of' },
          { value: 'hunted_by', label: 'Hunted By' },
          { value: 'hunts', label: 'Hunts' },
          { value: 'serves', label: 'Serves' },
          { value: 'commanded_by', label: 'Commanded By' },
          { value: 'guards', label: 'Guards' },
          { value: 'inhabits', label: 'Inhabits' },
          { value: 'protects', label: 'Protects' },
          { value: 'threatens', label: 'Threatens' },
          { value: 'created_by', label: 'Created By' },
          { value: 'summoned_by', label: 'Summoned By' },
          { value: 'ally_of', label: 'Ally Of' },
          { value: 'defeated_by', label: 'Defeated By' },
          { value: 'possesses', label: 'Possesses' },
          { value: 'encountered_at', label: 'Encountered At' },
        ];
      case 'faction':
        return [
          ...baseTypes,
          { value: 'has_member', label: 'Has Member' },
          { value: 'led_by', label: 'Led By' },
          { value: 'founded_by', label: 'Founded By' },
          { value: 'allied_with', label: 'Allied With' },
          { value: 'rival_of', label: 'Rival Of' },
          { value: 'at_war_with', label: 'At War With' },
          { value: 'controls', label: 'Controls' },
          { value: 'based_in', label: 'Based In' },
          { value: 'owns', label: 'Owns' },
          { value: 'sponsors', label: 'Sponsors' },
          { value: 'opposes', label: 'Opposes' },
          { value: 'parent_faction', label: 'Parent Faction' },
          { value: 'subfaction_of', label: 'Subfaction Of' },
        ];
      default:
        return baseTypes;
    }
  };

  useEffect(() => {
    const loadAvailableEntries = async () => {
      try {
        setLoading(true);
        const all = await wikiService.getAllWikiEntries();
        const filtered = all.filter((entry) => entry.id !== entryId);
        setAvailableEntries(filtered);
      } catch (error) {
        console.error('Error loading entries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAvailableEntries();
  }, [entryId]);

  const handleAddRelationship = async () => {
    if (selectedEntry && relationshipType) {
      try {
        await wikiService.addRelationship(entryId, selectedEntry, relationshipType);
        onRelationshipChange();
        setSelectedEntry('');
        setRelationshipType('related');
      } catch (error) {
        console.error('Error adding relationship:', error);
      }
    }
  };

  return (
    <Box>
      <Button 
        variant="outlined" 
        onClick={() => setOpen(true)}
        sx={{
          borderColor: wikiTheme.primary,
          color: wikiTheme.primary,
          fontFamily: wikiTheme.font,
          '&:hover': {
            borderColor: '#e0a23d',
            backgroundColor: 'rgba(217, 141, 42, 0.05)',
          },
        }}
      >
        Add Relationship
      </Button>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: wikiTheme.mediumGray,
          },
        }}
      >
        <DialogTitle sx={{ color: wikiTheme.secondary, fontFamily: wikiTheme.font }}>Add Relationship</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Autocomplete
            value={availableEntries.find(e => e.id === selectedEntry) || null}
            onChange={(event, newValue) => {
              setSelectedEntry(newValue ? newValue.id : '');
            }}
            options={availableEntries}
            getOptionLabel={(option) => `${option.title} (${option.type})`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entry"
                placeholder="Search for an entry..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: wikiTheme.secondary,
                    fontFamily: wikiTheme.font,
                    '& fieldset': {
                      borderColor: wikiTheme.lightGray,
                    },
                    '&:hover fieldset': {
                      borderColor: wikiTheme.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: wikiTheme.primary,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: wikiTheme.tertiary,
                    fontFamily: wikiTheme.font,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: wikiTheme.primary,
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    color: wikiTheme.primary,
                  },
                }}
              />
            )}
            sx={{ mb: 2 }}
            ListboxProps={{
              sx: {
                backgroundColor: wikiTheme.mediumGray,
                '& .MuiAutocomplete-option': {
                  color: wikiTheme.secondary,
                  fontFamily: wikiTheme.font,
                  '&:hover': {
                    backgroundColor: wikiTheme.dark,
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: wikiTheme.lightGray,
                  },
                },
              },
            }}
          />

          <Autocomplete
            value={getRelationshipTypes().find(t => t.value === relationshipType) || null}
            onChange={(event, newValue) => {
              setRelationshipType(newValue ? newValue.value : 'related');
            }}
            options={getRelationshipTypes()}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Relationship Type"
                placeholder="Search relationship types..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: wikiTheme.secondary,
                    fontFamily: wikiTheme.font,
                    '& fieldset': {
                      borderColor: wikiTheme.lightGray,
                    },
                    '&:hover fieldset': {
                      borderColor: wikiTheme.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: wikiTheme.primary,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: wikiTheme.tertiary,
                    fontFamily: wikiTheme.font,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: wikiTheme.primary,
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    color: wikiTheme.primary,
                  },
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-tag': {
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.dark,
              },
            }}
            ListboxProps={{
              sx: {
                backgroundColor: wikiTheme.mediumGray,
                '& .MuiAutocomplete-option': {
                  color: wikiTheme.secondary,
                  fontFamily: wikiTheme.font,
                  '&:hover': {
                    backgroundColor: wikiTheme.dark,
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: wikiTheme.lightGray,
                  },
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpen(false)}
            sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRelationship}
            variant="contained"
            disabled={!selectedEntry || !relationshipType || loading}
            sx={{
              backgroundColor: wikiTheme.primary,
              color: wikiTheme.dark,
              fontFamily: wikiTheme.font,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e0a23d',
              },
              '&.Mui-disabled': {
                backgroundColor: wikiTheme.lightGray,
                color: wikiTheme.tertiary,
              },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelationshipEditor;
